"use client";
import { Track } from "@/src/lib/tracking";

import { useEffect, useRef } from "react";
import { useSnapshot } from "valtio";
import { usePathname } from "next/navigation";
import { deviceDetector, getItem, setItem } from "utils";
import { rootStore } from "@store/rootStore";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import {
  useBridge,
  extractResultId,
  getBridge,
  handleDeepLinkOpened,
} from "../lib/bridge";
import { MessageType, type DeepLinkOpenedData } from "@/src/lib/bridge/types";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { ROUTER_PATH } from "@config/router";
import { useMemberUser } from "./useMemberUser";
import { useNotificationPermission } from "./useNotificationPermission";
import { useAppPushSubscription } from "./useAppPushSubscription";
import { useChatRetainModal } from "./useChatRetainModal";
import { useIsPc } from "./useIsPc";
import { useInviteCodeCapture } from "./useInviteCodeCapture";
import { useShellRouter } from "./useShellAwareRouter";
import { getDeferredDeepLinkPullDelaysMs } from "../lib/bridge/deferredDeepLinkPull";
import { isLoginRoute } from "@/src/hooks/onboarding/isOnboardingRoute";
import { normalizeProductPathname } from "@utils/proxyclass/onboardingStepGateLogic";
import {
  resolvePushNotificationRoute,
  type PushNotificationClickedPayload,
} from "./resolvePushNotificationRoute";
import {
  inferPushEntryDomain,
  setPushEntryMarker,
} from "./push/pushEntryMarker";
import { trackPushNotificationClick } from "./push/trackPushNotificationClick";

/**
 * 根据 market/category 解析为路由 path 片段（crypto | us-stocks | hk-stocks）
 * @returns 可用的 path 片段，无法解析时返回 undefined
 */
function getPath(market: string, category: string): string | undefined {
  if (category === "token" || category === "crypto") {
    return "crypto";
  }
  if (category === "stock") {
    if (market === "US") {
      return "us-stocks";
    }
    if (market === "HK") {
      return "hk-stocks";
    }
  }
  return undefined;
}

export function useInitApp({
  isClientReady = true,
}: {
  isClientReady?: boolean;
} = {}): void {
  const { isLoggedIn, isFetching } = useSnapshot(rootStore.login);
  const { on, isInApp } = useBridge();
  const { memberApi } = useOspApiClientProvider();
  const [isPC] = useIsPc();
  const { data: memberInfo } = useMemberUser();
  const router = useShellRouter();
  const isApp = deviceDetector.isApp();
  const { data: notificationPermission } = useNotificationPermission();
  const { requestOpenRetainModalFromPush, tryAutoOpenRetainModal } =
    useChatRetainModal({ isClientReady });
  const { openAppPush } = useAppPushSubscription();
  const pathname = usePathname();
  /**
   * 邀请码捕获的第二个触发源 —— 邀请码也可以只挂在 query 上（`?inviteCode=`），
   * 光靠 `pathname` 依赖会漏掉「同一 pathname 下只换 query」的跳转，
   * 于是捕获返回 null、回落 cookie，把**上一个**码灌回 store（表现为「新码覆盖不掉旧码」）。
   *
   * 这里刻意不用 `useSearchParams()`：本 hook 挂在根客户端壳（`RootLayoutClient`）上，
   * 引入它会让整棵树在预渲染时退化为 CSR，波及全部静态官网页。
   * 渲染期读 `window.location.search` 只用作 effect 依赖、不进 DOM，故不影响 hydration。
   */
  const locationSearch =
    typeof window === "undefined" ? "" : window.location.search;
  const appInteractiveReadySent = useRef(false);
  const productPath = normalizeProductPathname(pathname ?? "");
  const onLoginPage = isLoginRoute(productPath);

  // 首屏可交互后通知 App 隐藏启动屏（/login 由 LoginPage 发 web-login-gate-ready）
  useEffect(() => {
    if (!isApp || appInteractiveReadySent.current) {
      return;
    }
    if (onLoginPage) {
      return;
    }
    if (isFetching) {
      return;
    }

    const timer = setTimeout(() => {
      if (appInteractiveReadySent.current) {
        return;
      }
      if (isFetching) {
        return;
      }
      appInteractiveReadySent.current = true;
      getBridge().sendAppInteractiveReady();
    }, 400);

    return () => clearTimeout(timer);
  }, [isApp, isFetching, onLoginPage]);

  // 邀请码捕获（path / query → cookie + store）见 useInviteCodeCapture。
  useInviteCodeCapture({
    pathname,
    search: locationSearch,
    isLoggedIn,
    memberApi,
  });

  // 首次进入 App 且已登录、已授权通知时，自动开启推送订阅（仅一次）
  useEffect(() => {
    if (!isApp) {
      return;
    }
    if (
      !getItem(LOCAL_STORAGE_KEYS.IS_FIRST_LOAD) &&
      isLoggedIn &&
      notificationPermission?.enabled
    ) {
      openAppPush();
      setItem(LOCAL_STORAGE_KEYS.IS_FIRST_LOAD, "true");
    }
  }, [isApp, isLoggedIn, notificationPermission?.enabled, openAppPush]);

  // 登录后尝试一次 auto retain；真正何时允许打开由 retain hook 内部的
  // shouldShowRenew / push 优先级 / RootLayoutClient.isClientReady 一起决定。
  useEffect(() => {
    if (!isLoggedIn || isPC) {
      return;
    }
    tryAutoOpenRetainModal();
  }, [isLoggedIn, tryAutoOpenRetainModal, isPC]);

  // 推送点击路由：与原生约定 type 跳转对应页面
  useEffect(() => {
    if (!isInApp || !on || !router) {
      return;
    }
    const unsubscribe = on(
      MessageType.PUSH_NOTIFICATION_CLICKED,
      (data: PushNotificationClickedPayload) => {
        const pushType = data?.type;
        // 原写法是 `[GTM_CLICK] name`（少一层方括号，与其它 GTM 标记格式不一致）。
        // data 是对象，按规范 §3.2 只留可用于定位的标量。
        Track.event("push_notification_clicked", {
          push_type: pushType,
        });
        // 处理会员挽留弹窗
        if (pushType?.startsWith("fcm.body.member_retention.push.")) {
          /**
           * push 点击不会直接弹 retain modal，而是先登记一个待处理请求。
           * 真实弹窗时机交给 useChatRetainModal 内部统一处理：
           * - 最多等待 5s 让 ownerInfo / memberInfo hydration 完成
           * - push 与 auto 共用同一个会话级展示锁，避免连续弹两次
           */
          requestOpenRetainModalFromPush();
          return;
        }

        // V3.0.2：召回 / TRACKER → Brief / Plan / Desk 详情
        const v302Route = resolvePushNotificationRoute(data);
        if (v302Route) {
          trackPushNotificationClick(data);
          if (!v302Route.startsWith(ROUTER_PATH.BRIEF)) {
            setPushEntryMarker(
              inferPushEntryDomain({
                domain:
                  typeof data.domain === "string" ? data.domain : undefined,
                routePath: v302Route,
                pushType,
              }),
            );
          }
          router.push(v302Route);
          return;
        }

        switch (pushType) {
          case "fcm.title.opendata.hottheme": {
            const path = getPath(data.market ?? "", data.category ?? "");
            if (path && data.slug) {
              router.push(`${ROUTER_PATH.MARKET}/${path}/${data.slug}`);
            }
            break;
          }
          case "fcm.title.mixture.ai_pick_publish":
            router.push(ROUTER_PATH.ALPHA_PICK_CURRENT_PICKS);
            break;
          case "fcm.title.mixture.ai_pick_deep_analysis":
            if (memberInfo?.isFree) {
              router.push(ROUTER_PATH.ALPHA_PICK_CURRENT_PICKS);
            } else if (data.link) {
              router.push(`${ROUTER_PATH.ALPHA_PICK_PICKS}/${data.link}`);
            }
            break;
          case "fcm.title.mixture.ai_pick_price_increase":
            if (data.link) {
              router.push(`${ROUTER_PATH.ALPHA_PICK_PICKS}/${data.link}`);
            }
            break;
          case "fcm.title.mixture.ai_pick_related_news":
            const path = getPath(data.market ?? "", data.category ?? "");
            router.push(`${ROUTER_PATH.NEWS}/${path}/${data.link}`);
            break;
        }
      },
    );
    return unsubscribe;
  }, [isInApp, memberInfo?.isFree, on, requestOpenRetainModalFromPush, router]);

  // deeplink 路由：原生只上报“解析后的最终 URL”，参数映射和跳转由 Web 接管
  const lastHandledDeepLinkKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isInApp || !on || !router) {
      return;
    }

    let cancelled = false;
    let deepLinkConsumed = false;

    const handleDeepLinkPayload = (
      data: DeepLinkOpenedData,
      via: "push" | "pull",
    ) => {
      if (!data?.url && !extractResultId(data)) {
        return;
      }
      Track.log("info", "DEEPLINK", "consume payload", {
        via,
        resultId: extractResultId(data),
        source: data?.source,
        platform: data?.platform,
      });
      handleDeepLinkOpened(data, via, {
        router,
        lastHandledDeepLinkKeyRef,
      });
      deepLinkConsumed = true;
    };

    const pullLastDeepLink = (delayMs: number) => {
      if (cancelled || deepLinkConsumed) {
        return Promise.resolve();
      }
      Track.log("info", "DEEPLINK", "pull attempt", { delayMs });
      return getBridge()
        .getLastDeepLink({ timeoutMs: 8000 })
        .then((data) => {
          if (cancelled || deepLinkConsumed) {
            return;
          }
          const resultId = extractResultId(data);
          if (data?.url || resultId) {
            handleDeepLinkPayload(data, "pull");
            return;
          }
          Track.log("info", "DEEPLINK", "pull empty", { delayMs });
        })
        .catch((error) => {
          Track.log("info", "DEEPLINK", "pull unavailable", {
            delayMs,
            error: error instanceof Error ? error.message : String(error),
          });
        });
    };

    const unsubscribe = on(
      MessageType.DEEP_LINK_OPENED,
      (data: DeepLinkOpenedData) => {
        Track.log("info", "DEEPLINK", "push received", {
          resultId: extractResultId(data),
          source: data?.source,
        });
        handleDeepLinkPayload(data, "push");
      },
    );

    const pullDelaysMs = getDeferredDeepLinkPullDelaysMs(
      getBridge().getPlatform(),
    );
    Track.log("info", "DEEPLINK", "pull schedule started", {
      platform: getBridge().getPlatform(),
      delaysMs: String(pullDelaysMs),
    });
    const pullTimers = pullDelaysMs.map((delayMs) =>
      setTimeout(() => {
        void pullLastDeepLink(delayMs);
      }, delayMs),
    );

    return () => {
      cancelled = true;
      for (const timer of pullTimers) {
        clearTimeout(timer);
      }
      unsubscribe();
    };
  }, [isInApp, on, router]);
}
